import React, { useEffect, useState } from 'react';
import {
  Box,
  makeStyles,
  IconButton,
  SvgIcon,
  useTheme,
  CircularProgress
} from '@material-ui/core';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PropTypes from 'prop-types';
import clsx from 'clsx';

const useStyles = makeStyles(theme => ({
  root: {
    borderRadius: '10px',
    overflow: 'hidden'
  },
  playerContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  playerButton: {
    color: theme.palette.primary.main,
    '&:hover': {
      color: theme.palette.text.secondary
    }
  },
  loading: {
    backgroundColor: theme.palette.background.dark,
    display: 'grid',
    placeItems: 'center'
  },
  visiblePlayer: {
    height: '100%',
    width: '100%'
  },
  hiddenPlayer: {
    display: 'none'
  }
}));

function YoutubePlayer({
  src,
  onClickLeft,
  onClickRight,
  onVideoError,
  onVideoEnd,
  hide,
  ...props
}) {
  const classes = useStyles();
  const theme = useTheme();
  const [player, setPlayer] = useState(null);

  function onPlayerReady(event) {
    event.target.playVideo();
    event.target.setVolume(100);
  }

  function onError(event) {
    try {
      onVideoError();
    } catch (error) {
      return;
    }
  }

  function onStateChange(event) {
    switch (event.data) {
      case window.YT.PlayerState.ENDED:
        if (onVideoEnd) return onVideoEnd();
      default:
        return;
    }
  }

  const onYoutubeIframeAPIReady = () => {
    console.log(`[Youtube Player] Loading Video Id ${src}`);

    const newPlayer = new window.YT.Player('youtube-player', {
      videoId: src,
      playerVars: {
        disablekb: 0,
        autoplay: 1
      },
      events: {
        onReady: onPlayerReady,
        onStateChange,
        onError
      }
    });

    setPlayer(newPlayer);
  };

  useEffect(() => {
    if (!src) return;

    if (!window.YT) {
      window.onYouTubeIframeAPIReady = onYoutubeIframeAPIReady;

      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';

      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    } else {
      if (player) {
        console.log(`[Youtube Player] Loading Video Id ${src}`);

        player.loadVideoById(src);
      } else {
        onYoutubeIframeAPIReady();
      }
    }
  }, [src]);

  useEffect(() => {
    if (!player || !player.loadVideoById) return;
    console.log(`[Youtube Player] Loading Video Id ${src}`);
    player.loadVideoByid(src);
  }, [player]);

  useEffect(() => {
    if (!player || !player.pauseVideo || !player.playVideo) return;

    player.pauseVideo();

    if (!hide) player.playVideo();
  }, [hide]);

  return (
    <Box
      className={clsx({
        [classes.playerContainer]: !props.classes.playerContainer,
        [props.classes.playerContainer]: !!props.classes.playerContainer
      })}
      id="yt-player-container"
    >
      <IconButton
        size="medium"
        onClick={onClickLeft}
        className={clsx(props.classes.controlButtonLeft)}
      >
        <SvgIcon
          component={ChevronLeftIcon}
          className={clsx({
            [classes.playerButton]: !props.classes.controlButtonLeft,
            [props.classes.controlButtonLeft]: !!props.classes.controlButtonLeft
          })}
          sx={{
            fontSize: 40,
            color: theme.palette.primary.main,
            '&:hover': { color: theme.palette.text.secondary }
          }}
        />
      </IconButton>
      <Box className={clsx(props.classes.root, classes.root)}>
        <div
          className={clsx({
            [classes.visiblePlayer]: src,
            [classes.hiddenPlayer]: !src
          })}
          id={'youtube-player'}
        ></div>
        {!src && (
          <Box
            className={clsx({
              [props.classes.loading]: !!props.classes.loading,
              [classes.loading]: !props.classes.loading
            })}
            sx={{ display: 'flex' }}
          >
            <CircularProgress />
          </Box>
        )}
      </Box>
      <IconButton
        size="medium"
        onClick={onClickRight}
        className={clsx(props.classes.controlButtonRight)}
      >
        <ChevronRightIcon
          className={clsx({
            [classes.playerButton]: !props.classes.controlButtonRight,
            [props.classes.controlButtonRight]: !!props.classes
              .controlButtonRight
          })}
          sx={{
            fontSize: 40,
            color: theme.palette.primary.main,
            '&:hover': { color: theme.palette.text.secondary }
          }}
        />
      </IconButton>
    </Box>
  );
}

YoutubePlayer.propTypes = {
  classes: PropTypes.object,
  src: PropTypes.string
};

YoutubePlayer.defaultProps = {
  src: null,
  classes: {}
};

export default YoutubePlayer;
